import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, AlertTriangle } from "lucide-react";
import type { RemoteCommandCatalogEntry } from "../../../../api/maintenance";

interface ActionPickerProps {
  catalog: RemoteCommandCatalogEntry[];
  value: string | null;
  onChange: (action: string) => void;
  disabled?: boolean;
}

// Canonical group ordering — matches the design doc's grouping in §5.
const GROUP_ORDER = [
  "Sessions",
  "Service",
  "Configuration",
  "Diagnostics",
  "Smart Charging",
  "Local Auth List",
  "Firmware",
  "Vendor",
];

const ActionPicker: React.FC<ActionPickerProps> = ({ catalog, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusedIdx, setFocusedIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => catalog.find((e) => e.action === value) || null,
    [catalog, value],
  );

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filter = (e: RemoteCommandCatalogEntry) =>
      !q ||
      e.title.toLowerCase().includes(q) ||
      e.action.toLowerCase().includes(q) ||
      e.description.toLowerCase().includes(q) ||
      e.group.toLowerCase().includes(q);

    const map = new Map<string, RemoteCommandCatalogEntry[]>();
    for (const entry of catalog) {
      if (!filter(entry)) continue;
      const arr = map.get(entry.group) ?? [];
      arr.push(entry);
      map.set(entry.group, arr);
    }
    // Stable ordering
    const orderedGroups: { group: string; items: RemoteCommandCatalogEntry[] }[] = [];
    for (const g of GROUP_ORDER) {
      const items = map.get(g);
      if (items) orderedGroups.push({ group: g, items });
    }
    // Any unknown groups go at the end. (forEach avoids Map iterator semantics
    // that need --downlevelIteration on CRA's default es5 target.)
    map.forEach((items, g) => {
      if (!GROUP_ORDER.includes(g)) orderedGroups.push({ group: g, items });
    });
    return orderedGroups;
  }, [catalog, query]);

  // Flat list for keyboard navigation
  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setFocusedIdx(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  // Reset focus when filter changes
  useEffect(() => {
    setFocusedIdx(0);
  }, [query]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (!open && (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = flat[focusedIdx];
      if (pick) {
        onChange(pick.action);
        setOpen(false);
      }
    }
  };

  return (
    <div className="rcmd-picker" ref={containerRef} onKeyDown={handleKey}>
      <button
        type="button"
        className={`rcmd-picker-input ${open ? "is-open" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
            <span style={{ fontWeight: 500 }}>{selected.title}</span>
            <span style={{ fontSize: 11, color: "#888" }}>{selected.group} • {selected.action}</span>
          </span>
        ) : (
          <span className="rcmd-picker-placeholder">Select OCPP action…</span>
        )}
        <ChevronDown size={16} style={{ color: "#888", flexShrink: 0 }} />
      </button>

      {open && (
        <div className="rcmd-picker-dropdown" role="listbox">
          <div className="rcmd-picker-search">
            <div style={{ position: "relative" }}>
              <Search size={12} style={{ position: "absolute", left: 6, top: 8, color: "#888" }} />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search 20 actions…"
                style={{ paddingLeft: 24 }}
              />
            </div>
          </div>

          {flat.length === 0 && (
            <div style={{ padding: 16, color: "#888", fontSize: 13 }}>No matches</div>
          )}

          {grouped.map((g) => (
            <div className="rcmd-picker-group" key={g.group}>
              <div className="rcmd-picker-group-label">{g.group}</div>
              {g.items.map((item) => {
                const idx = flat.indexOf(item);
                return (
                  <div
                    key={item.action}
                    role="option"
                    aria-selected={item.action === value}
                    className={`rcmd-picker-item ${idx === focusedIdx ? "is-focused" : ""}`}
                    onMouseEnter={() => setFocusedIdx(idx)}
                    onClick={() => {
                      onChange(item.action);
                      setOpen(false);
                    }}
                  >
                    <div className="rcmd-picker-item-title">
                      <span>{item.title}</span>
                      {item.destructive && (
                        <span className="rcmd-destructive-badge">
                          <AlertTriangle size={9} /> Destructive
                        </span>
                      )}
                    </div>
                    <div className="rcmd-picker-item-desc">{item.description}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionPicker;
